import { Controller } from "@hotwired/stimulus"

// 任意のリストをクライアントサイドで並べ替える汎用 controller。
//
// 並べ替え対象は DOM に描画済みの item のみ (サーバ側ページネーションとは独立)。
// 値はソートボタンではなく item 側の data 属性 (data-sort-<key>) から読む。
//
// マークアップ例:
//   div data-controller="list-sort"
//        data-list-sort-key-value="num" data-list-sort-type-value="number"
//        data-list-sort-direction-value="desc"
//        data-list-sort-active-class="bg-emerald-50"
//     button data-list-sort-target="button" data-action="list-sort#sort"
//            data-list-sort-key-param="num" data-list-sort-type-param="number"
//            data-list-sort-direction-param="desc"
//       span ラベル
//       span data-list-sort-arrow="true"   ← 矢印を差し込みたい場所 (任意)
//     ul data-list-sort-target="list"
//       li data-list-sort-target="item" data-sort-num="..." data-sort-size="..."
//
// - key-value / type-value / direction-value はサーバ側が出力した初期並び順を表す。
//   key が宣言されていれば connect 時に一度その順へ正規化する (Turbo 復元で DOM 順が
//   崩れていてもインジケータと一致させるため)。宣言が無ければ DOM 順をそのまま使う。
// - 同じ key を再クリックすると昇順/降順をトグルする。別 key なら param の既定方向で始める。
// - 同値の item は安定ソートで元の DOM 順を保つ。
export default class extends Controller {
  static targets = ["list", "item", "button"]
  static classes = ["active"]
  static values = {
    key: String,
    type: { type: String, default: "text" },
    direction: { type: String, default: "asc" },
    locale: { type: String, default: "ja" },
    numeric: { type: Boolean, default: true },
    ascSymbol: { type: String, default: "▲" },
    descSymbol: { type: String, default: "▼" },
    neutralSymbol: { type: String, default: "⇅" },
  }

  connect() {
    if (this.keyValue) this.reorder()
    this.refreshIndicators()
  }

  sort(event) {
    const { key, type, direction } = event.params

    if (key === this.keyValue) {
      this.directionValue = this.directionValue === "asc" ? "desc" : "asc"
    } else {
      this.keyValue = key
      this.typeValue = type || "text"
      this.directionValue = direction || (this.typeValue === "number" ? "desc" : "asc")
    }

    this.reorder()
    this.refreshIndicators()
  }

  reorder() {
    if (!this.hasListTarget) return

    const factor = this.directionValue === "asc" ? 1 : -1
    const sorted = [...this.itemTargets].sort((a, b) => factor * this.compareValues(a, b))

    // appendChild は既存ノードを移動するだけ (複製しない)。fragment で reflow を 1 回にまとめる。
    const fragment = document.createDocumentFragment()
    sorted.forEach(item => fragment.appendChild(item))
    this.listTarget.appendChild(fragment)
  }

  compareValues(a, b) {
    const av = this.sortValueOf(a)
    const bv = this.sortValueOf(b)

    // number 型は欠損/非数値を 0 とみなす (汎用利用では欠損行が最小値側へ寄る点に注意)。
    if (this.typeValue === "number") {
      return (parseFloat(av) || 0) - (parseFloat(bv) || 0)
    }
    return av.localeCompare(bv, this.localeValue, { numeric: this.numericValue })
  }

  sortValueOf(item) {
    return item.dataset[this.datasetKey] ?? ""
  }

  // 単一トークンの key 前提: "num" → dataset["sortNum"] (= data-sort-num)。
  get datasetKey() {
    const key = this.keyValue
    return `sort${key.charAt(0).toUpperCase()}${key.slice(1)}`
  }

  refreshIndicators() {
    this.buttonTargets.forEach(button => {
      const active = button.dataset.listSortKeyParam === this.keyValue
      button.setAttribute("aria-pressed", active ? "true" : "false")

      if (this.hasActiveClass) {
        this.activeClasses.forEach(klass => button.classList.toggle(klass, active))
      }

      const arrow = button.querySelector("[data-list-sort-arrow]")
      if (arrow) arrow.textContent = this.symbolFor(active)
    })
  }

  symbolFor(active) {
    if (!active) return this.neutralSymbolValue
    return this.directionValue === "asc" ? this.ascSymbolValue : this.descSymbolValue
  }
}
